import React, { useState } from "react";
import { api } from "@/lib/api";
import { Building2, Users, Gift, Check, Send } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1600891964092-4316c288032e?auto=format&fit=crop&w=1920&q=85";

export default function CorporatePage() {
  const [form, setForm] = useState({
    contact_name: "", company: "", email: "", phone: "",
    employees_count: "", budget_range: "", occasion: "", message: "",
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.contact_name || !form.company || !form.email || !form.phone) {
      setErr("Completa los datos básicos");
      return;
    }
    setLoading(true);
    try {
      await api.post("/corporate/inquiry", {
        ...form,
        employees_count: form.employees_count ? parseInt(form.employees_count, 10) : null,
      });
      setSent(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al enviar");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center p-6" data-testid="corporate-success">
        <div className="text-center max-w-lg">
          <div className="w-20 h-20 mx-auto bg-arre-red/20 border border-arre-red flex items-center justify-center mb-6">
            <Check className="w-10 h-10 text-arre-red" strokeWidth={2} />
          </div>
          <h1 className="font-display text-5xl tracking-wider uppercase text-white leading-none">¡Gracias <span className="text-arre-red">compare!</span></h1>
          <p className="font-serif-display italic text-lg text-white/70 mt-4">
            Recibimos tu solicitud. En menos de 24 horas te contactamos con una cotización personalizada para tu empresa.
          </p>
          <a href="/" className="mt-8 inline-block btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase px-6 py-3">
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-arre-black min-h-screen" data-testid="corporate-page">
      {/* Hero */}
      <section className="relative py-20 border-b border-arre-border overflow-hidden grain">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url('${HERO}')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-arre-black/80 to-arre-black" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-arre-red/15 border border-arre-red/40 text-arre-red text-[10px] tracking-[0.35em] uppercase mb-4">
            <Building2 className="w-3 h-3" />
            Programa corporativo
          </div>
          <h1 className="font-display text-5xl md:text-9xl tracking-wider uppercase text-white leading-[0.85]">
            Regalos<br />
            <span className="text-arre-red italic font-serif-display normal-case tracking-tight">para empresas</span>
          </h1>
          <p className="font-serif-display italic text-xl md:text-2xl text-white/85 mt-6 max-w-3xl">
            Consiente a tu equipo, cierra cuentas con clientes, o celebra fin de año con la mejor carne sonorense. Cotización personalizada, logística en todo México.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        {/* Value props */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {[
            { icon: Users, title: "Pedidos por volumen", desc: "Desde 20 hasta 5,000+ unidades. Descuentos por mayoreo." },
            { icon: Gift, title: "Branding custom", desc: "Incluimos tarjeta personalizada con tu logo y mensaje." },
            { icon: Building2, title: "Facturación fiscal", desc: "Factura electrónica con tu RFC, condiciones de pago flexibles." },
          ].map((v, i) => (
            <div key={i} className="bg-arre-surface border border-arre-border p-6">
              <v.icon className="w-7 h-7 text-arre-red mb-3" strokeWidth={1.5} />
              <div className="font-display text-xl tracking-wider uppercase text-white leading-tight">{v.title}</div>
              <p className="text-sm text-white/60 mt-2">{v.desc}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-arre-surface border border-arre-border p-8 md:p-10" data-testid="corporate-form">
          <h2 className="font-display text-3xl md:text-4xl tracking-wider uppercase text-white mb-2">Solicita tu cotización</h2>
          <p className="text-sm text-white/60 mb-6">Llena el formulario y te contactamos en menos de 24h.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Tu nombre *" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="corp-name" />
            <Field label="Empresa *" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="corp-company" />
            <Field label="Correo corporativo *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="corp-email" />
            <Field label="Teléfono *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="corp-phone" />
            <Field label="¿Cuántos empleados/destinatarios?" type="number" value={form.employees_count} onChange={(v) => setForm({ ...form, employees_count: v })} testid="corp-employees" />
            <Select label="Presupuesto estimado" value={form.budget_range} onChange={(v) => setForm({ ...form, budget_range: v })} testid="corp-budget" options={[
              { v: "", l: "Selecciona..." },
              { v: "<50k", l: "Menos de $50,000" },
              { v: "50k-100k", l: "$50,000 – $100,000" },
              { v: "100k-200k", l: "$100,000 – $200,000" },
              { v: "200k+", l: "Más de $200,000" },
            ]} />
            <div className="md:col-span-2">
              <Select label="¿Para qué ocasión?" value={form.occasion} onChange={(v) => setForm({ ...form, occasion: v })} testid="corp-occasion" options={[
                { v: "", l: "Selecciona..." },
                { v: "navidad", l: "Navidad / Fin de año" },
                { v: "reconocimiento", l: "Reconocimiento a empleados" },
                { v: "clientes", l: "Regalo a clientes" },
                { v: "evento", l: "Evento corporativo" },
                { v: "otro", l: "Otro" },
              ]} />
            </div>
            <div className="md:col-span-2">
              <label className="block">
                <span className="text-xs tracking-[0.25em] uppercase text-white/60">Cuéntanos más</span>
                <textarea rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Fechas, logística de entrega, requerimientos especiales..."
                  className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red resize-none" />
              </label>
            </div>
          </div>
          {err && <p className="text-arre-red text-sm mt-4" data-testid="corp-error">{err}</p>}
          <button
            type="submit" disabled={loading}
            className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase px-8 py-4 mt-6 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="corp-submit"
          >
            <Send className="w-4 h-4" /> {loading ? "Enviando..." : "Solicitar cotización"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.25em] uppercase text-white/60">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red"
        data-testid={testid} />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.25em] uppercase text-white/60">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red"
        data-testid={testid}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
